import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import {safelyGetStorageItem} from '@vio/core/src/_web/utils/persistence/safelyGetStorageItem'
import {safelySetStorageItem} from '@vio/core/src/_web/utils/persistence/safelySetStorageItem'

const STORAGE_KEY = 'showPointsPrices'

interface PointsPriceContextValue {
  /** Display currency of the prices the app renders */
  currencyCode: string
  isOn: boolean
  setIsOn: (isOn: boolean) => void
}

export const PointsPriceContext = createContext<PointsPriceContextValue>({
  currencyCode: '',
  isOn: false,
  setIsOn: () => undefined
})

interface Props {
  currencyCode: string
  children: ReactNode
}

/**
 * Holds the "Show prices in Points" choice and the display currency it
 * applies to. Each app shell mounts it once, so the client and the checkout
 * share the same persisted choice instead of a store slice each.
 */
export const PointsPriceProvider = ({currencyCode, children}: Props) => {
  const [isOn, setIsOnState] = useState(false)

  // Read after mount: the server renders the toggle off, and the first client
  // render has to match it.
  useEffect(() => {
    setIsOnState(safelyGetStorageItem(localStorage, STORAGE_KEY) === 'true')
  }, [])

  const setIsOn = useCallback((next: boolean) => {
    safelySetStorageItem(localStorage, STORAGE_KEY, String(next))
    setIsOnState(next)
  }, [])

  const value = useMemo(
    () => ({currencyCode, isOn, setIsOn}),
    [currencyCode, isOn, setIsOn]
  )

  return (
    <PointsPriceContext.Provider value={value}>
      {children}
    </PointsPriceContext.Provider>
  )
}
